// Parses + aggregates the synced backup payload for the web diary view. The
// payload schema is owned by the Flutter app (lib/services/sync_service.dart):
// { prefs: {...}, people: [...], meals: [{ uid, name, kcal, mealType,
// consumedAt, personId, items }] }. Only the account holder's own meals are
// counted here — see isSelfMeal.
// Photos are NOT synced (they stay on-device), so the web view is text-only.
// Everything here is read-only — we never write back (the app's sync is
// last-write-wins with no merge, so editing from web could clobber the phone).

export type DiaryItem = {
  name: string;
  // Pre-formatted metric amount ("150 g" / "300 ml"), or undefined when the
  // item has none. Built from the blob's qty+unit; legacy blobs carried a
  // ready-made grams string instead.
  amount?: string;
  kcal: number;
  hidden?: boolean;
};

export type Meal = {
  uid: string;
  name: string;
  kcal: number;
  mealType: string;
  consumedAt: string; // ISO 8601 UTC
  hasPhoto: boolean;
  items: DiaryItem[];
};

export type Prefs = {
  goal?: number;
};

export type DaySummary = {
  key: string;        // YYYY-MM-DD in the viewer's local timezone
  date: Date;
  label: string;      // "Today" / "Yesterday" / locale date
  total: number;      // kcal
  meals: Meal[];      // sorted by time ascending
};

const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'];

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return typeof n === 'number' && isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export function parsePrefs(payload: unknown): Prefs {
  const p = (payload as { prefs?: Record<string, unknown> })?.prefs ?? {};
  const goal = num(p.goal, 0);
  return { goal: goal > 0 ? goal : undefined };
}

// New blobs carry numeric qty + unit ('g' | 'ml'); blobs pushed by older app
// builds carried a pre-formatted grams string. The web diary shows metric.
function itemAmount(i: Record<string, unknown>): string | undefined {
  if (typeof i.qty === 'number' && isFinite(i.qty)) {
    return `${Math.round(i.qty)} ${i.unit === 'ml' ? 'ml' : 'g'}`;
  }
  return typeof i.grams === 'string' && i.grams ? i.grams : undefined;
}

// The blob can hold meals for several people in the household (personId). The
// web diary is the account holder's own: absent or "self" is them, anything
// else belongs to an added person and is skipped here.
function isSelfMeal(meal: Record<string, unknown>): boolean {
  const pid = meal.personId;
  return pid === undefined || pid === null || pid === '' || pid === 'self';
}

export function parseMeals(payload: unknown): Meal[] {
  const raw = (payload as { meals?: unknown[] })?.meals;
  if (!Array.isArray(raw)) return [];
  const out: Meal[] = [];
  for (const m of raw) {
    const meal = m as Record<string, unknown>;
    if (!isSelfMeal(meal)) continue;
    const consumedAt = str(meal.consumedAt);
    if (!consumedAt) continue;
    const itemsRaw = Array.isArray(meal.items) ? meal.items : [];
    const items: DiaryItem[] = itemsRaw
      .map((it) => {
        const i = it as Record<string, unknown>;
        return {
          name: str(i.name),
          amount: itemAmount(i),
          kcal: num(i.kcal),
          hidden: i.hidden === true,
        };
      })
      .filter((i) => !i.hidden);
    out.push({
      uid: str(meal.uid, consumedAt),
      name: str(meal.name, 'Meal'),
      kcal: Math.round(num(meal.kcal)),
      mealType: str(meal.mealType, '—'),
      consumedAt,
      hasPhoto: meal.hasPhoto === true,
      items,
    });
  }
  return out;
}

// localDayKey returns the YYYY-MM-DD of an instant in the viewer's timezone, so
// "today" matches the user's wall clock rather than UTC.
function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayLabel(key: string, todayKey: string, yesterdayKey: string, date: Date): string {
  if (key === todayKey) return 'Today';
  if (key === yesterdayKey) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function groupByDay(meals: Meal[], now = new Date()): DaySummary[] {
  const todayKey = localDayKey(now);
  const yesterdayKey = localDayKey(new Date(now.getTime() - 86_400_000));

  const byKey = new Map<string, Meal[]>();
  for (const m of meals) {
    const d = new Date(m.consumedAt);
    if (isNaN(d.getTime())) continue;
    const key = localDayKey(d);
    (byKey.get(key) ?? byKey.set(key, []).get(key)!).push(m);
  }

  const days: DaySummary[] = [];
  for (const [key, dayMeals] of byKey) {
    dayMeals.sort((a, b) => a.consumedAt.localeCompare(b.consumedAt));
    // Stable order within identical timestamps by meal-type then name.
    dayMeals.sort((a, b) => {
      if (a.consumedAt !== b.consumedAt) return 0;
      const ai = MEAL_ORDER.indexOf(a.mealType.toLowerCase());
      const bi = MEAL_ORDER.indexOf(b.mealType.toLowerCase());
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
    const date = new Date(key + 'T00:00:00');
    days.push({
      key,
      date,
      label: dayLabel(key, todayKey, yesterdayKey, date),
      total: dayMeals.reduce((s, m) => s + m.kcal, 0),
      meals: dayMeals,
    });
  }
  days.sort((a, b) => b.key.localeCompare(a.key)); // newest first
  return days;
}

export type TrendPoint = { key: string; label: string; total: number; isToday: boolean };

// weeklySeries returns the last `days` calendar days (oldest→newest), filling
// gaps with zero so the chart has a fixed width.
export function weeklySeries(meals: Meal[], days = 7, now = new Date()): TrendPoint[] {
  const totals = new Map<string, number>();
  for (const m of meals) {
    const d = new Date(m.consumedAt);
    if (isNaN(d.getTime())) continue;
    const key = localDayKey(d);
    totals.set(key, (totals.get(key) ?? 0) + m.kcal);
  }
  const todayKey = localDayKey(now);
  const out: TrendPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86_400_000);
    const key = localDayKey(d);
    out.push({
      key,
      label: d.toLocaleDateString(undefined, { weekday: 'narrow' }),
      total: totals.get(key) ?? 0,
      isToday: key === todayKey,
    });
  }
  return out;
}
